<script setup lang="ts">
import { onMounted, ref } from 'vue';
import axios from 'axios';
import UserService from '@/service/user.service';
import retry from 'async-retry'; // optional but recommended

// Reactive variables for title and message
const title = ref<string>('');
const message = ref<string>('');
const pushTokens = ref<Record<string, string>[]>([]);

// Error handling
const error = ref<string | null>(null);

const sendNotification = async (expoPushToken: string): Promise<void> => {
  try {
    await retry(
      async () => {
        await axios.post('http://localhost:3000/send-notification', {
          title: title.value,
          message: message.value,
          expoPushToken,
        });
      },
      {
        retries: 2,
        minTimeout: 500,
        factor: 2,
      },
    );
    console.log(`✅ Sent to ${expoPushToken}`);
  } catch (err) {
    console.error(`❌ Failed to send to ${expoPushToken}`, err);
  }
};

const sendNotificationsToAllUsers = async () => {
  const tokens = pushTokens.value.map((t) => t.token);
  const CONCURRENCY_LIMIT = 10;

  // eslint-disable-next-line no-promise-executor-return
  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  for (let i = 0; i < tokens.length; i += CONCURRENCY_LIMIT) {
    const chunk = tokens.slice(i, i + CONCURRENCY_LIMIT);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(chunk.map(sendNotification));
    // eslint-disable-next-line no-await-in-loop
    await delay(100); // throttle to avoid hitting server too hard
  }

  console.log('🎉 All notifications processed');
};

onMounted(async () => {
  pushTokens.value = await UserService.getUserPushTokens();
  console.log(`Total push tokens: ${pushTokens.value.length}`);
});
</script>

<template>
  <div class="w-screen px-10">
    <form
      class="flex flex-col border border-neutral-600 rounded-xl items-center justify-center"
      @submit.prevent="sendNotificationsToAllUsers"
    >
      <div class="flex flex-col gap-2 w-96">
        <label for="title">Title</label>
        <input
          id="title"
          v-model="title"
          class="rounded-lg border border-black"
          type="text"
          placeholder="Enter title"
        >
      </div>

      <div class="flex flex-col gap-2 w-96">
        <label for="message">Message</label>
        <textarea
          id="message"
          v-model="message"
          class="rounded-lg border border-black"
          placeholder="Enter message"
        />
      </div>

      <button
        type="submit"
        class="w-96 py-3 bg-blue-900 text-white text-center rounded-xl"
      >
        Send
      </button>
    </form>

    <div v-if="error">
      {{ error }}
    </div>
  </div>
</template>

<style scoped>

label {
  margin-bottom: 5px;
}

input,
textarea {
  margin-bottom: 15px;
  padding: 8px;
}

</style>
